/**
 * TrialBanner Component
 *
 * Displays a banner when user is in a trial period, showing days remaining
 * and encouraging upgrade.
 */

import React, { useState } from 'react';
import { Clock, X, Sparkles } from 'lucide-react';

interface TrialBannerProps {
  daysRemaining: number;
  planName: string;
  onDismiss?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({ daysRemaining, planName, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);

  const isUrgent = daysRemaining <= 3;
  const isExpiringSoon = daysRemaining <= 7;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) {
    return null;
  }

  return (
    <div
      className={`relative rounded-lg p-4 mb-6 border ${
        isUrgent
          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
      }`}
      role="alert"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={`shrink-0 p-2 rounded-full ${
            isUrgent
              ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
              : 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
          }`}
        >
          {isUrgent ? <Clock className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3
            className={`text-sm font-semibold ${
              isUrgent ? 'text-amber-800 dark:text-amber-300' : 'text-blue-800 dark:text-blue-300'
            }`}
          >
            {isUrgent
              ? `Your trial ends in ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}!`
              : `You're trying the ${planName} plan`}
          </h3>
          <p
            className={`mt-1 text-sm ${
              isUrgent ? 'text-amber-700 dark:text-amber-400' : 'text-blue-700 dark:text-blue-400'
            }`}
          >
            {isUrgent
              ? `Your trial ends soon.`
              : isExpiringSoon
                ? `You have ${daysRemaining} days left in your trial.`
                : `Enjoy your ${daysRemaining}-day free trial.`}
          </p>
        </div>

        {/* Dismiss Button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            className={`shrink-0 p-1 rounded-full transition-colors ${
              isUrgent
                ? 'text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                : 'text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/40'
            }`}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Progress indicator */}
      {!isUrgent && (
        <div className="mt-3 flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
          <div className="flex-1 h-1.5 bg-blue-200 dark:bg-blue-900 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 dark:bg-blue-400 transition-all duration-300"
              style={{ width: `${Math.max(0, 100 - (daysRemaining / 14) * 100)}%` }}
            />
          </div>
          <span>{daysRemaining} days left</span>
        </div>
      )}
    </div>
  );
};

export default TrialBanner;
